/**
 * Student home — list of assignments for the logged-in student.
 * Each assignment links to the submit page (or the latest result).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';

interface AssignmentRow {
  id: string;
  title: string;
  problem_set_id: string;
  due_date: string;
}

export function StudentHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await api.get<{ assignments: AssignmentRow[] }>('/api/assignments');
        if (active) setAssignments(data.assignments);
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <main className="page">
      <header className="header">
        <h1 data-testid="student-home-title">
          {user?.name}님의 숙제
          <span className="badge">Student</span>
        </h1>
        <button onClick={handleLogout} className="btn btn-ghost" data-testid="logout-button">
          Logout
        </button>
      </header>

      {loading && (
        <div className="loading" role="status" aria-label="불러오는 중">
          <span>불러오는 중…</span>
        </div>
      )}
      {error && <p className="error-text" role="alert">{error}</p>}

      {!loading && assignments.length === 0 && (
        <div className="empty-state" data-testid="no-assignments">
          <p>출제된 숙제가 없습니다</p>
        </div>
      )}

      <ul className="list-stack" data-testid="assignment-list" aria-label="숙제 목록">
        {assignments.map((a) => (
          <li key={a.id} className="list-item" data-testid={`assignment-${a.id}`}>
            <div className="list-item-header">
              <strong>{a.title}</strong>
              {a.due_date && (
                <span className="due-date">
                  마감 {a.due_date.slice(0, 10)}
                </span>
              )}
            </div>
            <div className="list-item-actions">
              <Link
                to={`/student/assignments/${a.id}/submit`}
                className="btn btn-gold"
                data-testid={`submit-${a.id}`}
              >
                제출하기
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
